import {
  Breadcrumbs,
  Button,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import ChatIcon from "@material-ui/icons/Chat";
import InventoryIcon from "@material-ui/icons/Storage";
import ShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import { showPopupSnackbar } from "../../../redux/actions/snackbarActions";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getInvoiceIssuerData,
  getInvoiceIssuers,
  getToken,
  paymentStates,
  requestErrorHandler,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";
import DetailTabs from "../../DeviceDetails/DetailTabs";

const server = EnvSettings.server;
const supplierInfoFields = [
  "contactName",
  "contactEmail",
  "companyAddress",
  "companyVatId",
];

const clone = (data) => JSON.parse(JSON.stringify(data || {}));

function SupplierCompany(props) {
  const { id } = props.computedMatch.params;
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [company, setCompany] = useState();
  const [orig, setOrig] = useState();
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);

  const getCompany = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        companyId: id,
      }),
    };
    fetch(server + "/get_supplier_companies", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setCompany(result.company);
          setOrig(clone(result.company));
        }
      });
  };

  const editCompany = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        contactData: {
          companyId: company.id,
          companyName: company.name,
          location: company.location,
          additionalInfo: company.additionalInfo || {},
        },
      }),
    };
    fetch(server + "/edit_supplier_company", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setOrig(clone(company));
          dispatch(showPopupSnackbar(result));
        }
      });
  };

  const editAdditionalInfo = (key, value) => {
    setCompany({
      ...company,
      additionalInfo: {
        ...(company.additionalInfo || {}),
        [key]: value,
      },
    });
  };

  const formatGenericDevices = (data) => {
    if (!data || !data.length) {
      return "";
    }
    if (data.length > 1) {
      var count = 0;
      data.forEach((d) => (count = count + d.count));
      return t("count") + ":" + count;
    }
    return data.map((v) => v.count + " x " + v.name).join(", ");
  };

  const formatPaymentStatus = (data) => {
    if (!data) {
      return "";
    }
    var state = paymentStates.find((o) => o.value === data);
    return state ? t("paymentStates." + state.value) : data;
  };

  const additionalInfoFields = useMemo(() => {
    return [
      ...new Set([
        ...supplierInfoFields,
        ...Object.keys(company?.additionalInfo || {}),
      ]),
    ];
  }, [company]);

  useEffect(() => {
    if (id) {
      getCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  return company && orig ? (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href={"/cpanel/resupply"}>
            {t("resupplyOrders.header")}
          </Link>
          <Link color="inherit" href={"/cpanel/resupply/suppliers"}>
            {t("resupplyOrders.supplierCompanies")}
          </Link>
          <Typography color="textPrimary">{orig.name}</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Title>{orig.name}</Title>
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label={t("contactInfo.companyName")}
                inputProps={{ maxLength: 255 }}
                value={company.name || ""}
                onChange={(e) => {
                  setCompany({
                    ...company,
                    name: e.target.value,
                  });
                }}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label={t("contactInfo.location")}
                value={company.location || ""}
                onChange={(e) => {
                  setCompany({
                    ...company,
                    location: e.target.value,
                  });
                }}
              />
            </Grid>
            {additionalInfoFields.map((field) => (
              <Grid item sm={4} xs={12} key={field}>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline={field === "companyAddress"}
                  rows={field === "companyAddress" ? 3 : undefined}
                  label={
                    supplierInfoFields.includes(field)
                      ? t("contactInfo." + field)
                      : field
                  }
                  value={company.additionalInfo?.[field] || ""}
                  onChange={(e) => {
                    editAdditionalInfo(field, e.target.value);
                  }}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                onClick={editCompany}
                variant="contained"
                color="primary"
                disabled={JSON.stringify(orig) === JSON.stringify(company)}
              >
                {t("save")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <DetailTabs
          activeTabs={[
            {
              label: (
                <>
                  <ShoppingCartIcon /> {t("resupplyOrders.header")}
                </>
              ),
              content: (
                <SortableTable
                  downloadButton={true}
                  header={t("resupplyOrders.header")}
                  tableLayout={"fixed"}
                  headCells={[
                    {
                      id: "Notes",
                      label: "",
                      width: 32,
                      hideFromCsv: true,
                    },
                    {
                      id: "updatedAt",
                      numeric: false,
                      disablePadding: false,
                      label: t("updatedAt"),
                    },
                    {
                      id: "createdAt",
                      numeric: false,
                      disablePadding: false,
                      label: t("createdAt"),
                    },
                    {
                      id: "User.fullName",
                      numeric: false,
                      disablePadding: false,
                      label: t("createdBy"),
                    },
                    {
                      id: "buyer",
                      numeric: false,
                      disablePadding: false,
                      label: t("resupplyOrders.buyer"),
                    },
                    {
                      id: "number",
                      numeric: false,
                      disablePadding: false,
                      label: t("orders.orderNumber"),
                    },
                    {
                      id: "paymentStatus",
                      numeric: false,
                      disablePadding: false,
                      label: t("orders.paymentStatus"),
                    },
                    {
                      id: "genericDevices",
                      numeric: false,
                      disablePadding: false,
                      label: t("items"),
                    },
                    {
                      id: "totalPrice",
                      numeric: false,
                      disablePadding: false,
                      label: t("orders.totalPrice"),
                    },
                  ]}
                  defaultSortRow={"updatedAt"}
                  handleRowClick={(row) => {
                    history.push("/cpanel/resupply/" + row.number);
                  }}
                  getRowStyle={(data) => {
                    var obj = { cursor: "pointer" };
                    var state = paymentStates.find(
                      (f) => data.paymentStatus === f.value,
                    );
                    if (state && state.color) {
                      return { ...obj, backgroundColor: state.color };
                    }
                    return obj;
                  }}
                  specialDataFormatting={[
                    {
                      id: "Notes",
                      format: (data) => {
                        if (data && data.find((f) => f.User)) {
                          return (
                            <Tooltip title={t("activeComments")}>
                              <ChatIcon
                                color="primary"
                                style={{ fontSize: 20 }}
                              />
                            </Tooltip>
                          );
                        }
                        return "";
                      },
                    },
                    {
                      id: "updatedAt",
                      format: (data, d, row) => {
                        if (row.Notes && row.Notes.length) {
                          const max = row.Notes.reduce((prev, current) =>
                            prev && prev.id > current.id ? prev : current,
                          );
                          return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                        }
                        return formatTimeWithTZOffset(data, ECABIN);
                      },
                    },
                    {
                      id: "createdAt",
                      format: (data) => {
                        return formatTimeWithTZOffset(data, ECABIN);
                      },
                    },
                    {
                      id: "genericDevices",
                      format: formatGenericDevices,
                    },
                    {
                      id: "paymentStatus",
                      format: formatPaymentStatus,
                    },
                    {
                      id: "buyer",
                      format: (data) => {
                        if (!data) {
                          return data;
                        }
                        return (
                          getInvoiceIssuerData(invoiceIssuers, data)?.name ||
                          data
                        );
                      },
                    },
                    {
                      id: "totalPrice",
                      format: (data, d, row) => {
                        return data || data === 0
                          ? data + " " + (row.currency || "")
                          : "";
                      },
                    },
                  ]}
                  tableData={company.ResupplyOrders}
                  rowsPerPage={50}
                />
              ),
            },
            {
              label: (
                <>
                  <InventoryIcon /> {t("stockModule.stock")}
                </>
              ),
              content: (
                <SortableTable
                  downloadButton={true}
                  header={t("stockModule.stock")}
                  tableLayout={"fixed"}
                  headCells={[
                    {
                      id: "StockQuantity.StockItem.sku",
                      numeric: false,
                      disablePadding: false,
                      label: t("stockModule.sku"),
                    },
                    {
                      id: "StockQuantity.StockItem.name",
                      numeric: false,
                      disablePadding: false,
                      label: t("name"),
                    },
                    {
                      id: "StockQuantity.Warehouse.name",
                      numeric: false,
                      disablePadding: false,
                      label: t("stockModule.warehouse"),
                    },
                    {
                      id: "productCode",
                      numeric: false,
                      disablePadding: false,
                      label: t("stockModule.supplierProductCode"),
                    },
                    {
                      id: "deliveryTime",
                      numeric: false,
                      disablePadding: false,
                      label: t("stockModule.deliveryTime"),
                    },
                    {
                      id: "url",
                      numeric: false,
                      disablePadding: false,
                      label: "URL",
                    },
                  ]}
                  defaultSortRow={"StockQuantity.StockItem.sku"}
                  handleRowClick={() => {}}
                  specialDataFormatting={[
                    {
                      id: "url",
                      formatReturnsElement: true,
                      format: (data) => {
                        return data ? (
                          <Link
                            href={data}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {data}
                          </Link>
                        ) : (
                          ""
                        );
                      },
                    },
                  ]}
                  tableData={company.StockItemSupplierDetails}
                  rowsPerPage={50}
                />
              ),
            },
          ]}
        />
      </Grid>
    </Grid>
  ) : null;
}

export default SupplierCompany;
